import { fetchRoles } from '../../api/users';

// Role dropdown options shared by NewUser and UserEdit (N-01) — the two forms
// must always offer the same list.
//
// Canonical roles the admin panel offers, in display order. Worker/QA are
// intentionally excluded: QA has no behavior anywhere, and the worker tier is
// 'Operator' (which the API special-cases for the Reports-To / verifier
// sign-off requirement). Auditor is the read-only Final Review tier
// (2026-08-23 spec): sees everything, signs Auditor Approvals, changes
// nothing else. buildRoleOptions filters DB roles to this set.
export const CANON_ROLES = ['Admin', 'Manager', 'Operator', 'Auditor'];

// Used only when the GET /Role fetch fails or the DB has none of the canon set.
export const FALLBACK_ROLES = CANON_ROLES.map(n => ({ id: n, name: n }));

// Live role data carries both 'Admin' and 'Administrator' (mixed case) — every
// admin-name comparison in the panel goes through here, mirroring the API's
// normalization (middleware/requireRole).
export const isAdminRoleName = (name?: string): boolean =>
  ['admin', 'administrator'].includes(String(name || '').trim().toLowerCase());

/**
 * Pure filter from GET /Role rows to dropdown options. Legacy GET /Role
 * returns ALL companies' roles (company populated); tenant GET /t/:slug/Role
 * returns only the tenant's — so rows are filtered to companyId, then to the
 * canonical set (case-insensitive, keeping the DB's own spelling so a save
 * sends a name the API's resolver will find). Falls back to the full
 * canonical set if the DB happens to have none of them.
 */
export const buildRoleOptions = (roles: any[], companyId: string): { id: string; name: string }[] => {
  const names: string[] = [];
  (roles || []).forEach((r: any) => {
    const roleCompany = r.company && (r.company._id || r.company);
    if (roleCompany && String(roleCompany) !== String(companyId)) return;
    if (r.name && !names.includes(r.name)) names.push(r.name);
  });
  const allowed = CANON_ROLES
    .map(c => names.find(n => n.toLowerCase() === c.toLowerCase()))
    .filter((n): n is string => !!n);
  return (allowed.length > 0 ? allowed : CANON_ROLES).map(n => ({ id: n, name: n }));
};

export const loadRoleOptions = async (companyId: string): Promise<{ id: string; name: string }[]> =>
  buildRoleOptions(await fetchRoles(), companyId);
